import { EXPLORE_ROUTES } from "../exploreRoutes";
import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import ChallengeStatsCard from "./ChallengeStatsCard";
import BattleSelectionCard from "./BattleSelectionCard";
import CoinChooseOption from "./CoinChooseOption";
import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";
import { useGamificationBattle } from "../../hooks/useGamificationBattle";
import {
  buildQuestionBankBackTarget,
  buildQuestionBankFlowState,
} from "../../questionBank/qbankFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import "./SelectBattle.css";

const SelectBattle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCoinOptionsOpen, setIsCoinOptionsOpen] = useState(false);
  const flowState = useMemo(
    () => buildGamificationFlowState(location.state, getStoredGamificationFlowState()),
    [location.state]
  );
  const startBattleBackTarget = useMemo(
    () =>
      flowState?.gamificationBackTarget?.pathname
        ? flowState.gamificationBackTarget
        : buildGamificationBackTarget(GAMIFICATION_ROUTES.start, flowState),
    [flowState]
  );

  const { data, isLoading, isFetching, isError, error } = useGamificationBattle();

  useEffect(() => {
    persistGamificationFlowState({
      ...flowState,
      userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
      user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
      rank: Number(data?.rank || flowState?.rank || 0),
      score: Number(data?.score || flowState?.score || 0),
      gamificationBackTarget: startBattleBackTarget,
    });
  }, [data?.rank, data?.score, data?.userCoins, flowState, startBattleBackTarget]);
  const isPending = isLoading || isFetching;
  const loadError = isError
    ? error?.response?.data?.message || error?.message || "Unable to load battles."
    : "";
  const leaderboardState = useMemo(
    () => ({
      ...flowState,
      userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
      user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
      rank: Number(data?.rank || flowState?.rank || 0),
      score: Number(data?.score || flowState?.score || 0),
      gamificationBackTarget: buildGamificationBackTarget(GAMIFICATION_ROUTES.selectBattle, {
        ...flowState,
        userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
        user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
        rank: Number(data?.rank || flowState?.rank || 0),
        score: Number(data?.score || flowState?.score || 0),
        gamificationBackTarget: startBattleBackTarget,
      }),
    }),
    [data?.rank, data?.score, data?.userCoins, flowState, startBattleBackTarget]
  );

  const handleSelectBattle = (battle) => {
    const selectedBattle = battle?.raw || battle;

    navigate(EXPLORE_ROUTES.subjects, {
      state: buildQuestionBankFlowState({
        ...flowState,
        userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
        user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
        rank: Number(data?.rank || flowState?.rank || 0),
        score: Number(data?.score || flowState?.score || 0),
        selectedBattle,
        selectedState: flowState?.selectedState,
        selectedCategory: flowState?.selectedCategory,
        state_id: Number(flowState?.state_id || flowState?.selectedState?.state_id || 13),
        category_id: Number(flowState?.category_id || flowState?.selectedCategory?.category_id || 6),
        qbankBackTarget: buildQuestionBankBackTarget(GAMIFICATION_ROUTES.selectBattle, {
          ...flowState,
          userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
          user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
          rank: Number(data?.rank || flowState?.rank || 0),
          score: Number(data?.score || flowState?.score || 0),
          selectedBattle,
          gamificationBackTarget: startBattleBackTarget,
        }),
      }),
    });
  };

  return (
    <>
      <HomeDashboardLayout showHeader={false} variant="battle-select-layout">
        <div className="qbl-page qbankList-page page-padding start-gamification ">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigate(startBattleBackTarget.pathname, {
                        replace: true,
                        state: startBattleBackTarget.state,
                      })
                    }
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title">Challenge</h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            <ChallengeStatsCard
              rank={data?.rank || 0}
              coins={data?.userCoins || 0}
              score={data?.score || 0}
              isLoading={isPending}
              onLeaderboardClick={() =>
                navigate(GAMIFICATION_ROUTES.leaderboard, {
                  state: leaderboardState,
                })
              }
              onCoinsClick={() => setIsCoinOptionsOpen(true)}
            />

            {!isPending && loadError ? (
              <p className="choose-state__status choose-state__status--error">{loadError}</p>
            ) : null}

            {!loadError ? (
              <BattleSelectionCard
                battles={data?.battles || []}
                isLoading={isPending}
                onSelectBattle={handleSelectBattle}
              />
            ) : null}
          </div>
        </div>

        <CoinChooseOption
          open={isCoinOptionsOpen}
          onClose={() => setIsCoinOptionsOpen(false)}
          onStoreClick={() => {
            setIsCoinOptionsOpen(false);
            navigate(GAMIFICATION_ROUTES.coinStore, {
              state: leaderboardState,
            });
          }}
          onHistoryClick={() => {
            setIsCoinOptionsOpen(false);
            navigate(GAMIFICATION_ROUTES.coinHistory, {
              state: leaderboardState,
            });
          }}
        />
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
    </>
  );
};

export default SelectBattle;


